import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, Mail, Phone, Building2, Globe, Pencil, Filter } from 'lucide-react'
import { formatDate } from '@/lib/dateUtils'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PageHeader, StatCard, StatCardGroup } from '@/components/shared'
import { emailContactService } from '@/services/emailContactService'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'
import ContactFormDialog from '@/components/MarketingEmail/ContactFormDialog'

const consentVariant = {
  SUBSCRIBED: 'default',
  PENDING: 'secondary',
  UNSUBSCRIBED: 'outline',
}

const SegmentsTab = ({ contactId }) => {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery(
    ['email-contact-segments', contactId],
    () => emailContactService.getContactSegments(contactId),
    { onError: () => toast.error('Failed to fetch segments') }
  )
  const segments = data?.data?.segments || []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Matching Segments</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="bg-muted/50 font-medium">Name</TableHead>
              <TableHead className="bg-muted/50 font-medium">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(2)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                </TableRow>
              ))
            ) : segments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="h-24 text-center text-muted-foreground">
                  This contact does not match any segments.
                </TableCell>
              </TableRow>
            ) : (
              segments.map((s) => (
                <TableRow key={s.id} className="border-border">
                  <TableCell>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-primary font-medium"
                      onClick={() => navigate(`/marketing-email/segments/${s.id}`)}
                    >
                      {s.name}
                    </Button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{s.description || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

const ContactDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)

  const { data, isLoading } = useQuery(['email-contact', id], () => emailContactService.getContactById(id), {
    onError: () => toast.error('Failed to fetch contact'),
  })
  const { data: analyticsData } = useQuery(
    ['email-contact-analytics', id],
    () => emailContactService.getContactAnalytics(id)
  )
  const { data: fieldsData } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields)
  const customFieldDefinitions = fieldsData?.data?.fields || []

  const contact = data?.data?.contact
  const summary = analyticsData?.data?.summary
  const activity = analyticsData?.data?.activity || []

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="text-center py-12 text-muted-foreground">Contact not found.</div>
    )
  }

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email

  return (
    <div className="space-y-6">
      <PageHeader
        title={fullName}
        description={contact.email}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/contacts')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Contacts
            </Button>
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </>
        }
      />

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="segments">
            <Filter className="mr-2 h-3.5 w-3.5" />
            Segments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" /> {contact.email}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-4 w-4" /> {contact.phone || '—'}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4" /> {contact.company_name || '—'}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="h-4 w-4" /> {contact.country || '—'}
              </div>
              <div>
                <span className="text-muted-foreground mr-2">Consent:</span>
                <Badge variant={consentVariant[contact.consent_status] || 'secondary'}>{contact.consent_status}</Badge>
              </div>
              {Object.entries(contact.custom_fields || {}).map(([key, value]) => (
                <div key={key}>
                  <span className="text-muted-foreground mr-2">{key}:</span>
                  {String(value ?? '—')}
                </div>
              ))}
            </CardContent>
          </Card>

          {summary && (
            <StatCardGroup>
              <StatCard title="Emails Sent" value={summary.sent} contentClassName="p-4" />
              <StatCard title="Delivered" value={summary.delivered} contentClassName="p-4" />
              <StatCard title="Opened" value={summary.opened} contentClassName="p-4" />
              <StatCard title="Clicked" value={summary.clicked} contentClassName="p-4" />
            </StatCardGroup>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Campaign Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="bg-muted/50 font-medium">Campaign</TableHead>
                    <TableHead className="bg-muted/50 font-medium">Status</TableHead>
                    <TableHead className="bg-muted/50 font-medium">Sent</TableHead>
                    <TableHead className="bg-muted/50 font-medium">Opened</TableHead>
                    <TableHead className="bg-muted/50 font-medium">Clicked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                        No campaign activity yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    activity.map((row) => (
                      <TableRow key={row.id} className="border-border">
                        <TableCell className="font-medium">{row.campaign?.name || '—'}</TableCell>
                        <TableCell><Badge variant="secondary">{row.status}</Badge></TableCell>
                        <TableCell>{row.sent_at ? formatDate(row.sent_at) : '—'}</TableCell>
                        <TableCell>{row.opened_at ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{row.clicked_at ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="segments" className="mt-4">
          <SegmentsTab contactId={id} />
        </TabsContent>
      </Tabs>

      <ContactFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        contact={contact}
        customFieldDefinitions={customFieldDefinitions}
        onSuccess={() => {
          queryClient.invalidateQueries(['email-contact', id])
          queryClient.invalidateQueries('email-contacts')
        }}
      />
    </div>
  )
}

export default ContactDetail
